import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  PackagePlus,
  Upload,
  ListChecks,
  ClipboardList,
  Bot,
  Shield,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../AuthProvider';
import UsageWidget from '../UsageWidget';
import NavItem from './NavItem';
import RailTooltip, { RailTooltipProvider } from './RailTooltip';
import ShipmentsRailMenu from './ShipmentsRailMenu';

/** Below this width the desktop sidebar becomes an icon rail (13" laptops and narrower windows). */
const RAIL_QUERY = '(max-width: 1279.98px)';
/** Wider screens can collapse it by hand; remembered per browser. */
const COLLAPSE_KEY = 'carbonroute_sidebar_collapsed';

function useIsRail() {
  const [rail, setRail] = useState(() => typeof window !== 'undefined' && window.matchMedia(RAIL_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(RAIL_QUERY);
    const on = () => setRail(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return rail;
}

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
}

function SectionLabel({ children, collapsed }) {
  if (collapsed) return <div className="mx-3 my-2 border-t border-border/70" aria-hidden="true" />;
  return (
    <div className="px-3 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * App sidebar, as in the approved concept: brand, grouped navigation (Shipments / Scope 3 /
 * Assistant / Workspace) and a footer with plan usage and the signed-in profile.
 * `drawer` = rendered inside the mobile drawer (always full width, never the icon rail).
 *
 * Collapsed (icon rail): automatic below 1280px, or by hand on wider screens. In the rail every
 * name is a floating label outside the sidebar (no sideways scroll), and Shipments is one icon
 * that opens New Shipment / Bulk Upload beside it.
 */
export default function Sidebar({ onItemClick, drawer = false }) {
  const { user, logout } = useAuth();
  const rail = useIsRail();
  const [manualCollapsed, setManualCollapsed] = useState(readCollapsed);
  const collapsed = !drawer && (rail || manualCollapsed);
  const canToggle = !drawer && !rail;

  const toggleCollapsed = () => {
    setManualCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  };

  const name = user?.full_name || user?.email || 'Account';
  const org = user?.company_name || user?.email;
  const initials = (user?.full_name || user?.email || 'U')
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const signOut = () => { logout(); window.location.href = '/login'; };

  const toggleButton = canToggle && (
    <RailTooltip label="Expand sidebar" disabled={!collapsed}>
      <button
        type="button"
        onClick={toggleCollapsed}
        className="sidebar-toggle flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        title={collapsed ? undefined : 'Collapse sidebar'}
      >
        {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
      </button>
    </RailTooltip>
  );

  return (
    <RailTooltipProvider delayDuration={120} skipDelayDuration={400}>
      <aside
        className={cn(
          'app-chrome sidebar flex h-full flex-col overflow-hidden border-r',
          collapsed ? 'w-[76px]' : drawer ? 'w-full' : 'w-[244px]'
        )}
        aria-label="Navigation"
      >
        {/* Brand */}
        <div className={cn('flex border-b border-border', collapsed ? 'flex-col items-center gap-2 px-2 py-4' : 'items-center gap-2.5 px-4 py-4')}>
          <img
            src="/assets/Arantree_logo_upscaled.png"
            alt="Arantree"
            className="h-9 w-9 flex-shrink-0 rounded-[10px] bg-white object-contain p-[3px] shadow-sm"
          />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold leading-tight tracking-tight text-primary">CarbonRoute</p>
              <p className="truncate text-[11px] text-muted-foreground">by Arantree</p>
            </div>
          )}
          {toggleButton}
        </div>

        {/* Navigation. overflow-x-hidden: nothing in here may ever widen the sidebar. */}
        <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden pb-3 pt-2', collapsed ? 'px-2' : 'px-3')}>
          <NavItem icon={LayoutDashboard} label="Dashboard" to="/dashboard" collapsed={collapsed} onClick={onItemClick} />

          <SectionLabel collapsed={collapsed}>Shipments</SectionLabel>
          {collapsed ? (
            <ShipmentsRailMenu onItemClick={onItemClick} />
          ) : (
            <>
              <NavItem icon={PackagePlus} label="New Shipment" to="/shipments/new" collapsed={false} onClick={onItemClick} />
              <NavItem icon={Upload} label="Bulk Upload" to="/shipments/bulk" collapsed={false} onClick={onItemClick} />
            </>
          )}

          <SectionLabel collapsed={collapsed}>Scope 3</SectionLabel>
          <NavItem icon={ListChecks} label="My Activities" to="/scope3/activities" collapsed={collapsed} onClick={onItemClick} />
          <NavItem icon={ClipboardList} label="Commute Surveys" to="/scope3/surveys" collapsed={collapsed} onClick={onItemClick} />

          <SectionLabel collapsed={collapsed}>Assistant</SectionLabel>
          <NavItem icon={Bot} label="Ask Trace" to="/shanthi" collapsed={collapsed} onClick={onItemClick} />

          {user?.is_admin && (
            <>
              <SectionLabel collapsed={collapsed}>Workspace</SectionLabel>
              <NavItem icon={Shield} label="Admin" to="/admin" collapsed={collapsed} onClick={onItemClick} />
            </>
          )}
        </nav>

        {/* Footer: plan usage + profile */}
        {!user?.is_guest && (
          <div className={cn('space-y-3 border-t border-border py-3', collapsed ? 'px-2' : 'px-3')}>
            {!collapsed && <UsageWidget className="sidebar-usage flex w-full" />}
            <div className={cn('flex items-center gap-2.5', collapsed ? 'flex-col' : 'px-1')}>
              <RailTooltip label={`${name}${org && org !== name ? ` · ${org}` : ''}`} disabled={!collapsed}>
                <div
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
                  role="img"
                  aria-label={`Signed in as ${name}`}
                  tabIndex={collapsed ? 0 : -1}
                >
                  {initials}
                </div>
              </RailTooltip>
              {!collapsed && (
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="truncate text-sm font-semibold text-foreground" title={name}>{name}</p>
                  <p className="truncate text-xs text-muted-foreground" title={org}>{org}</p>
                </div>
              )}
              <RailTooltip label="Sign out" disabled={!collapsed}>
                <button
                  type="button"
                  onClick={signOut}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive"
                  title={collapsed ? undefined : 'Sign out'}
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </RailTooltip>
            </div>
          </div>
        )}
      </aside>
    </RailTooltipProvider>
  );
}
